/**
 * Wallet balance from the on-box app API (ASTPP is source of truth, AUD-native).
 *
 * Display-only. Transactions and ledger history still come from Supabase
 * (`useCallingBalance`) because the API doesn't expose them. This hook exists
 * solely to eliminate FX drift on the balance pill.
 */
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { fetchAppApiBalance, type AppApiBalance } from "@/lib/app-api";

const CALLING_BALANCE_CHANGED_EVENT = "elefa:calling-balance-changed";

export interface AppApiBalanceState {
  /** AUD balance as a decimal number. null while loading or on auth failure. */
  balanceAud: number | null;
  /** Same value in whole cents for legacy consumers. */
  balanceAudCents: number | null;
  currency: "AUD";
  provisioned: boolean;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

export function useAppApiBalance(): AppApiBalanceState {
  const qc = useQueryClient();

  // Refetch when a top-up / call debit fires the app-wide event, matching
  // the existing useCallingBalance behaviour so Stripe webhooks still poke us.
  useEffect(() => {
    const refresh = () => qc.invalidateQueries({ queryKey: ["app-api-balance"] });
    window.addEventListener(CALLING_BALANCE_CHANGED_EVENT, refresh);
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "SIGNED_OUT") {
        qc.invalidateQueries({ queryKey: ["app-api-balance"] });
      }
    });
    return () => {
      window.removeEventListener(CALLING_BALANCE_CHANGED_EVENT, refresh);
      subscription.unsubscribe();
    };
  }, [qc]);

  const { data, isLoading, error, refetch } = useQuery<AppApiBalance | null>({
    queryKey: ["app-api-balance"],
    queryFn: async ({ signal }) => {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session?.access_token) return null;
      return await fetchAppApiBalance(signal);
    },
    staleTime: 30_000,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const balanceAud = typeof data?.balance === "number" ? data.balance : null;
  const balanceAudCents = balanceAud == null ? null : Math.round(balanceAud * 100);

  return {
    balanceAud,
    balanceAudCents,
    currency: "AUD",
    provisioned: data?.provisioned ?? false,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Format an AUD decimal as "A$X.XX". null → "—" per spec (no wrong guess).
 */
export function formatAudBalance(balanceAud: number | null): string {
  if (balanceAud == null || !Number.isFinite(balanceAud)) return "—";
  return `A$${balanceAud.toFixed(2)}`;
}
